import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { FormResponseService } from './form-response.service';
import { HandleConditionalLogicService } from './handle-conditional-logic.service';
import { MultiSectionFormPreviewApiService } from './multi-section-form-preview-api.service';
import { IQuesField, ISectionInfo } from './multi-section-form-preview.models';
import { MultiSectionFormPreviewService } from './multi-section-form-preview.service';
import { SubmitSurveyConfirmationComponent } from '../submit-survey-confirmation/submit-survey-confirmation.component';
import { ScoresAnalysisComponent } from '../scores-analysis/scores-analysis.component';
import { FooterButtonsComponent } from './footer-buttons/footer-buttons.component';
import { CurrentSectionAllFieldsPreviewComponent } from './current-section-all-fields-preview/current-section-all-fields-preview.component';
import { PreviewBreadcrumbsComponent } from './preview-breadcrumbs/preview-breadcrumbs.component';
import { LegendInformationComponent } from './legend-information/legend-information.component';
import { ListSectionsAndFieldsComponent } from './list-sections-and-fields/list-sections-and-fields.component';
import { NgClass } from '@angular/common';
import { AlertNoLogicComponent } from './alert-no-logic/alert-no-logic.component';

@Component({
    selector: 'app-multi-section-form-preview',
    templateUrl: './multi-section-form-preview.component.html',
    styleUrls: ['./multi-section-form-preview.component.css'],
    standalone: true,
    imports: [
    AlertNoLogicComponent,
    NgClass,
    ListSectionsAndFieldsComponent,
    LegendInformationComponent,
    PreviewBreadcrumbsComponent,
    CurrentSectionAllFieldsPreviewComponent,
    FooterButtonsComponent,
    ScoresAnalysisComponent,
    SubmitSurveyConfirmationComponent
],
})
export class MultiSectionFormPreviewComponent implements OnInit {
  @Input() allFieldsList!: IQuesField[];
  @Input() allSectionsList!: ISectionInfo[];
  @Input() isBuyerManagementPreview!: boolean;
  @Input() mode!: string;

  public msfps = inject(MultiSectionFormPreviewService);
  public msfapi = inject(MultiSectionFormPreviewApiService);
  public frs = inject(FormResponseService);
  public hcl = inject(HandleConditionalLogicService);

  isPreviewReady: boolean = false;

  ngOnInit() {
    this.msfps.isBuyerManagementPreview = Boolean(
      this.isBuyerManagementPreview
    );
    this.msfps.mode = this.mode ?? '';
    this.getConditionalLogicWithIds();

    this.msfps.clearAnswerForField$.subscribe((field)=>{
      if(field) this.frs.clearAnswerForTheQuestion(field)
    })
  }

  private _templateId = '';
  public get templateId() {
    let localStorageTemplateId = localStorage.getItem('TemplateId');
    return (this._templateId = localStorageTemplateId ?? '');
  }

  private _formTitle = '';
  public get formTitle() {
    let localStorageFormTitle = localStorage.getItem('Title');
    return (this._formTitle = localStorageFormTitle ?? '');
  }

  getConditionalLogicWithIds() {
    this.msfapi.getConditionalLogicJSON(this.templateId).subscribe({
      next: (resp) => {
        this.hcl.multiSectionFormConditionalLogicJSON = resp?.Data;
        this.onFetchConditionalLogicWithIds();
      },
      error: (err) => {
        if (window.location.origin.includes('localhost')) {
          this.hcl.multiSectionFormConditionalLogicJSON = [];
          this.onFetchConditionalLogicWithIds();
        }
      },
    });
  }

  onFetchConditionalLogicWithIds() {
    this.hcl.getConditionalLogicWithNames(
      this.hcl.multiSectionFormConditionalLogicJSON
    )
    this.msfps.allSections = this.allSectionsList ?? [];
    this.msfps.activeSecInfo = this.allSectionsList?.[0];
    this.msfps.toggleIndividualSectionFieldPreviewMode = this.msfps.activeSecInfo.DisplayType === 'One'
    this.msfps.allFields =
      this.allFieldsList?.map((field) => ({
        ...this.msfps.getAppropriateField(field),
        nodeLogic: this.hcl.getNodeLogicFor(field),
        isQnFieldVisibleOnNavListItems: Boolean(
          this.hcl.showFieldOnNavListItems(field)
        ),
        isQnFieldOptional: Boolean(
          this.hcl.isFieldOptional(field)
        ),
        isQnFieldVisibleOnPreviewArea: Boolean(
          this.hcl.showFieldOnPreviewArea(field)
        ),
        isAnswerModified: false,
        answered: this.msfps.doesDisableInputExistFor(field)
          ? Boolean(this.hcl.getNodeLogicFor(field)?.selectedAnswers?.length)
          : false,
        valid: false,
        isFieldBeingPreviewedIndividually:
          this.msfps.toggleIndividualSectionFieldPreviewMode,
      })) ?? [];

    if (this.msfps.allFields?.length) {
      this.msfps.fieldsSegregatedIntoSections = this.msfps.allFields?.reduce(
        (acc, currField) => {
          acc[currField.sectionId] = {
            isSectionCurrentlyBeingPreviewed: false,
            areSectionFieldsCollapsed:
              false,
            isSubSection: Boolean(
              this.msfps.getSection(currField.sectionId).ParentID
            ),
            isParentSectionCollapsed:
              false,
            isEntireSectionBeingPreviewed: false,
            isIndividualSectionFieldBeingPreviewed: this.msfps.getSection(currField.sectionId).DisplayType === 'One',
            sectionDetails: this.msfps.getSection(currField.sectionId),
            fieldsList: this.msfps.allFields?.filter(
              (field) => field?.sectionId === currField?.sectionId
            ),
          };
          return acc;
        },
        {}
      );
    }
    this.frs.getAlreadySavedAnswersIntoSupplierPreview();    
  }

  @Output() modalDismiss: EventEmitter<string> = new EventEmitter();
  dismiss(triggeredBy: string) {
    this.modalDismiss.emit(triggeredBy);
  }

  @Output() modalClose: EventEmitter<string> = new EventEmitter();
  close(triggeredBy: string) {
    this.modalClose.emit(triggeredBy);
  }

  ngOnDestroy() {
    this.frs.clearAnswersForAllTheQuestions();
  }
}
